import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import {
  useGetTaskTypeByIdQuery,
  useCreateTaskTypeMutation,
  useUpdateTaskTypeMutation,
  useLazyGetStatusesByModelQuery,
  useLazyGetStatusesQuery,
} from '../../store/api';
import { extractList } from '../../utils/response';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { toast } from 'react-toastify';
import { buildTaskTypeRelationParams } from './taskTypeApiHelpers';

const buildFormData = (values) => {
  const fd = new FormData();
  fd.append('status_id', String(values.status_id));
  fd.append('name', values.name ?? '');
  fd.append('color', values.color ?? '');
  fd.append('description', values.description ?? '');
  return fd;
};

const statusLabel = (s) => s.label ?? s.name ?? s.title ?? String(s.id);

function normalizeHexInput(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  if (t.startsWith('#')) return t.length <= 9 ? t : t.slice(0, 9);
  return `#${t.replace(/^#/, '')}`;
}

const CreateTaskType = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [description, setDescription] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchStatusesByModel] = useLazyGetStatusesByModelQuery();
  const [fetchStatusesList] = useLazyGetStatusesQuery();

  const taskTypeStatusTrigger = useCallback(
    () => ({
      unwrap: async () => {
        try {
          return await fetchStatusesByModel('TaskType').unwrap();
        } catch {
          return await fetchStatusesList({ paginate: 0 }).unwrap();
        }
      },
    }),
    [fetchStatusesByModel, fetchStatusesList]
  );

  const statusSelect = useLazySelectOptions(taskTypeStatusTrigger, {
    mapResult: (res) => {
      const list = extractList(res);
      const arr = Array.isArray(list) ? list : [];
      return arr.map((s) => ({
        id: s.id,
        label: statusLabel(s),
        raw: s,
      }));
    },
  });

  const loadStatuses = useCallback(
    () =>
      statusSelect.load().catch(() => {
        toast.error('Could not load statuses');
      }),
    [statusSelect]
  );

  const {
    data: typeData,
    isLoading: typeLoading,
    isError: typeError,
    error: typeErr,
  } = useGetTaskTypeByIdQuery(
    { id: editId, params: buildTaskTypeRelationParams() },
    { skip: !isEdit || !editId }
  );
  const [createTaskType, { isLoading: creating }] = useCreateTaskTypeMutation();
  const [updateTaskType, { isLoading: updating }] = useUpdateTaskTypeMutation();
  const submitting = creating || updating;
  const loadingPage = isEdit && (typeLoading || !hydrated);

  useEffect(() => {
    if (typeError) {
      toast.error(typeErr?.data?.message || 'Failed to load task type');
      navigate('/task-types');
    }
  }, [typeError, typeErr, navigate]);

  useEffect(() => {
    if (!isEdit || !typeData || hydrated) return;
    const record = typeData?.data ?? typeData;
    if (!record) return;

    setName(record.name ?? '');
    setColor(record.color && String(record.color).trim() ? normalizeHexInput(record.color) : '#3B82F6');
    setDescription(record.description ?? '');

    const st = record.status && typeof record.status === 'object' ? record.status : null;
    const sid = record.status_id ?? st?.id;
    if (sid) {
      if (st) {
        const opt = { id: sid, label: statusLabel(st), raw: st };
        setSelectedStatus(opt);
        statusSelect.setOptions((prev) => (prev.some((o) => o.id === sid) ? prev : [...prev, opt]));
      } else {
        const opt = statusSelect.options.find((o) => o.id === sid);
        setSelectedStatus(opt ?? { id: sid, label: String(sid), raw: {} });
      }
    } else {
      setSelectedStatus(null);
    }
    setHydrated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional dependency list
  }, [isEdit, typeData, statusSelect.options, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = {};
    if (!name.trim()) clientErrors.name = 'Name is required';
    if (!selectedStatus?.id) clientErrors.status_id = 'Status is required';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const values = {
      status_id: selectedStatus.id,
      name: name.trim(),
      color: color.trim(),
      description: description.trim(),
    };
    const fd = buildFormData(values);

    try {
      // No relations on write requests — the page navigates away from the response
      if (isEdit) {
        await updateTaskType({ id: editId, data: fd }).unwrap();
        toast.success('Task type updated');
      } else {
        await createTaskType({ data: fd }).unwrap();
        toast.success('Task type added');
      }
      navigate('/task-types');
    } catch (err) {
      applyApiError(
        err,
        isEdit ? 'Failed to update task type' : 'Failed to add task type'
      );
    }
  };

  const pickerValue = /^#([0-9A-Fa-f]{6})$/.test(color.trim()) ? color.trim() : '#000000';

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-types')} sx={{ mb: 2 }}>
          Back to task types
        </Button>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
          {isEdit ? 'Edit task type' : 'Add task type'}
        </Typography>

        <Card>
          <CardContent component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    clearError('name');
                  }}
                  required
                  fullWidth
                  {...fieldProps('name')}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <TextField
                    label="Color"
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      clearError('color');
                    }}
                    fullWidth
                    placeholder="#3B82F6"
                    error={!!errors.color}
                    helperText={errors.color || 'Hex color (e.g. #3B82F6)'}
                  />
                  <Box
                    component="label"
                    sx={{
                      mt: 1,
                      width: 48,
                      height: 40,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      flexShrink: 0,
                      bgcolor: pickerValue,
                      overflow: 'hidden',
                    }}
                  >
                    <input
                      type="color"
                      value={pickerValue}
                      onChange={(e) => setColor(e.target.value)}
                      style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    clearError('description');
                  }}
                  fullWidth
                  multiline
                  minRows={3}
                  {...fieldProps('description')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={statusSelect.options}
                  value={selectedStatus}
                  onChange={(_, v) => {
                    setSelectedStatus(v);
                    clearError('status_id');
                  }}
                  onOpen={loadStatuses}
                  loading={statusSelect.loading}
                  getOptionLabel={(opt) => (opt?.label != null ? opt.label : '')}
                  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      required
                      fullWidth
                      {...fieldProps('status_id')}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button type="submit" variant="contained" disabled={submitting}>
                  {submitting ? <CircularProgress size={22} /> : isEdit ? 'Save changes' : 'Add task type'}
                </Button>
                <Button type="button" variant="outlined" onClick={() => navigate('/task-types')} disabled={submitting}>
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CreateTaskType;
